import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api";
import { useParams } from "react-router-dom";

const KittyLedger = () => {
  const [kitties, setKitties] = useState([]);
  const [filteredKitties, setFilteredKitties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState("all"); // all, today, custom
  const [customDate, setCustomDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchKitties = async () => {
    try {
      const res = await api.get(`/kitty-ledger/${id}`);
      setKitties(res.data);
      setFilteredKitties(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching kitties:", err.response || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKitties();
  }, [id]);

  // Filter function
  const applyFilter = () => {
    if (!kitties?.data?.length) return;

    let filtered = [...kitties.data];

    if (dateFilter === "today") {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      filtered = filtered.filter(kitty => kitty.date === today);
    } 
    else if (dateFilter === "custom" && customDate) {
      filtered = filtered.filter(kitty => kitty.date === customDate);
    }
    else if (dateFilter === "range" && fromDate && toDate) {
      filtered = filtered.filter(kitty => {
        const kittyDate = new Date(kitty.date);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        return kittyDate >= from && kittyDate <= to;
      });
    }
    // For "all", no filtering needed

    setFilteredKitties(filtered);
  };

  // Apply filter when filter options change
  useEffect(() => {
    applyFilter();
  }, [dateFilter, customDate, fromDate, toDate, kitties]);

  // Reset filters
  const resetFilters = () => {
    setDateFilter("all");
    setCustomDate("");
    setFromDate("");
    setToDate("");
    setFilteredKitties(kitties?.data || []);
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  if (loading) {
    return <div className="p-6 text-center">Loading kitties...</div>;
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Kitty Ledger</h1>
      </div>

      {/* Date Filter Section */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow border">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Filter Type Select */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium mb-1">Filter by Date</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="all">All Dates</option>
              <option value="today">Today ({getTodayDate()})</option>
              <option value="custom">Specific Date</option>
              <option value="range">Date Range</option>
            </select>
          </div>

          {/* Custom Date Input */}
          {dateFilter === "custom" && (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium mb-1">Select Date</label>
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className="w-full border rounded px-3 py-2"
                max={getTodayDate()}
              />
            </div>
          )}

          {/* Date Range Inputs */}
          {dateFilter === "range" && (
            <>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium mb-1">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  max={getTodayDate()}
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium mb-1">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  max={getTodayDate()}
                  min={fromDate}
                />
              </div>
            </>
          )}

          {/* Reset Button */}
          {(dateFilter !== "all" || customDate || fromDate || toDate) && (
            <div>
              <button
                onClick={resetFilters}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>

        {/* Filter Status Info */}
        <div className="mt-3 text-sm text-gray-600">
          {dateFilter === "today" && (
            <span>Showing records for today: {getTodayDate()}</span>
          )}
          {dateFilter === "custom" && customDate && (
            <span>Showing records for: {customDate}</span>
          )}
          {dateFilter === "range" && fromDate && toDate && (
            <span>Showing records from {fromDate} to {toDate}</span>
          )}
          {dateFilter === "all" && (
            <span>Showing all records</span>
          )}
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-700">
        Showing {filteredKitties.length} of {kitties?.data?.length || 0} records
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border rounded-lg shadow">
          <thead>
            <tr className="bg-gray-100 border-b">
              <th className="px-4 py-2 text-left">ID</th>
              <th className="px-4 py-2 text-left">Kitty Name</th>
              <th className="px-4 py-2 text-left">Customer Name</th>
              <th className="px-4 py-2 text-left">Amount</th>
              <th className="px-4 py-2 text-left">Remark</th>
              <th className="px-4 py-2 text-left">Payment Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredKitties.length > 0 ? (
              filteredKitties.map((kitty, index) => (
                <tr key={kitty.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2">{index + 1}</td>
                  <td className="px-4 py-2">{kitty.kitty_name}</td>
                  <td className="px-4 py-2 capitalize">{kitty.full_name}</td>
                  <td className="px-4 py-2">₹{kitty.kity_amount}</td>
                  <td className="px-4 py-2">{kitty.remark || "-"}</td>
                  <td className="px-4 py-2">
                    {kitty.date} 
                    {kitty.date === getTodayDate() && (
                      <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Today</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-4 py-6 text-center text-gray-500">
                  No records found {dateFilter !== "all" ? "for the selected filter" : ""}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default KittyLedger;